// NOTICE: KAPTION-INTEGRATION
// call-transcript-search.API.1 / WEB.2 — one search hit as the result rows
// need it. The API shape (GET kaption/calls/search, and `call_results` in the
// global conversation search) is read only here, so a rename on the Rails
// side is a one-line change:
//   {call: {id, started_at, direction, duration_seconds, inbox: {id, name},
//           conversation_id},
//    contact: {id, name, thumbnail},
//    agents: [{id, name}], transcript_status,
//    snippets: [{segment_id, speaker, start_ms, text, marks: [[s, e]]}]}
import {
  AGENT_TONES,
  CUSTOMER_TONE,
  CUSTOMER_KEY,
} from './kaptionCallTranscriptModel';

const pick = (...values) => values.find(v => v !== undefined && v !== null);

const toIso = value => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number')
    return new Date(value < 1e12 ? value * 1000 : value).toISOString();
  return String(value);
};

// The text of a snippet split into plain and marked pieces from `marks`
// offsets (never HTML from the server — SNIP.2).
export const snippetPieces = (text = '', marks = []) => {
  const pieces = [];
  let cursor = 0;
  [...(marks || [])]
    .filter(m => Array.isArray(m) && m[1] > m[0])
    .sort((a, b) => a[0] - b[0])
    .forEach(([from, to]) => {
      const a = Math.max(from, cursor);
      const b = Math.min(to, text.length);
      if (b <= a) return;
      if (a > cursor) pieces.push({ text: text.slice(cursor, a), mark: false });
      pieces.push({ text: text.slice(a, b), mark: true });
      cursor = b;
    });
  if (cursor < text.length)
    pieces.push({ text: text.slice(cursor), mark: false });
  return pieces;
};

export const normalizeSearchResult = (raw = {}) => {
  const call = raw.call || {};
  const contact = raw.contact || {};
  const agents = (raw.agents || []).map((agent, index) => ({
    id: agent.id,
    name: agent.name || '',
    tone: AGENT_TONES[index % AGENT_TONES.length],
  }));
  const agentOf = speaker => {
    const match = /^user:(\d+)$/.exec(String(speaker || ''));
    return match ? agents.find(a => a.id === Number(match[1])) : null;
  };
  return {
    callId: pick(call.id, raw.call_id),
    conversationId: pick(call.conversation_id, null),
    startedAt: toIso(pick(call.started_at, call.ring_started_at, null)),
    direction: pick(call.direction, 'inbound'),
    durationSeconds: pick(call.duration_seconds, null),
    inboxName: pick(call.inbox?.name, call.inbox_name, ''),
    transcriptStatus: pick(call.transcript_status, raw.transcript_status, null),
    contactName: pick(contact.name, contact.phone_number, ''),
    contactAvatar: pick(contact.thumbnail, contact.avatar_url, ''),
    agents,
    snippets: (raw.snippets || []).map((snippet, index) => {
      const agent = agentOf(snippet.speaker);
      const isCustomer =
        !agent &&
        (snippet.speaker === CUSTOMER_KEY || snippet.speaker === 'contact');
      return {
        key: pick(snippet.segment_id, index),
        speaker: snippet.speaker,
        isCustomer,
        agentName: agent?.name || '',
        tone: agent?.tone || CUSTOMER_TONE,
        startMs: Number(pick(snippet.start_ms, 0)) || 0,
        pieces: snippetPieces(String(snippet.text || ''), snippet.marks),
      };
    }),
    // The shape normalizeCall reads, so a click can open the detail at once.
    callPayload: {
      id: pick(call.id, raw.call_id),
      conversation_id: pick(call.conversation_id, null),
      contact_name: pick(contact.name, ''),
      contact_avatar_url: pick(contact.thumbnail, ''),
      inbox_name: pick(call.inbox?.name, ''),
      direction: pick(call.direction, 'inbound'),
      status: pick(call.status, 'completed'),
      started_at: pick(call.started_at, null),
      duration_seconds: pick(call.duration_seconds, null),
      transcript_status: pick(
        call.transcript_status,
        raw.transcript_status,
        'ready'
      ),
    },
  };
};

// call-transcript-search.WEB.1 — the date presets → API `from` (ISO date).
export const DATE_PRESETS = ['today', '7', '30', '90'];
export const presetFrom = (preset, now = new Date()) => {
  if (!preset) return undefined;
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  if (preset !== 'today') from.setDate(from.getDate() - Number(preset));
  return from.toISOString();
};

// URL query ↔ search state (a search can be shared by its link).
export const searchStateFromQuery = (query = {}) => ({
  q: String(query.q || ''),
  date: DATE_PRESETS.includes(String(query.date)) ? String(query.date) : '',
  direction: ['inbound', 'outbound'].includes(query.direction)
    ? query.direction
    : '',
  agentId: query.agent_id ? Number(query.agent_id) || '' : '',
});

export const searchStateToQuery = state => {
  const query = {};
  if (state.q) query.q = state.q;
  if (state.date) query.date = state.date;
  if (state.direction) query.direction = state.direction;
  if (state.agentId) query.agent_id = String(state.agentId);
  return query;
};

export const searchApiParams = (state, now = new Date()) => {
  const params = {};
  const from = presetFrom(state.date, now);
  if (from) params.from = from;
  if (state.direction) params.direction = state.direction;
  if (state.agentId) params.agent_id = state.agentId;
  return params;
};
